import { cn } from "@/lib/cn";
import { Badge, type BadgeTono } from "./Badge";

export interface EtapaCronograma {
  id: number;
  nombre: string;
  detalle: string;
  /** Posición y largo de la etapa en el plan, en % del largo total. */
  izquierda: number;
  ancho: number;
  /** Avance real de la etapa, 0 a 100 (se acota). */
  avance: number;
  tono: BadgeTono;
  estado: string;
}

export interface CronogramaProps {
  etiqueta: string;
  etapas: EtapaCronograma[];
  /** Posición de hoy en % del largo del plan; `null` si cae fuera. */
  hoy?: number | null;
}

// Clases literales (Tailwind no detecta clases armadas concatenando strings)
const FONDO: Record<BadgeTono, string> = {
  neutro: "bg-borde",
  primario: "bg-primario-suave",
  exito: "bg-exito-suave",
  alerta: "bg-alerta-suave",
  peligro: "bg-peligro-suave",
};
const RELLENO: Record<BadgeTono, string> = {
  neutro: "bg-texto-suave",
  primario: "bg-primario",
  exito: "bg-exito",
  alerta: "bg-alerta",
  peligro: "bg-peligro",
};

/** Cronograma tipo Gantt: una barra por etapa ubicada en el tiempo según el plan y rellena según el avance real. */
export function Cronograma({ etiqueta, etapas, hoy = null }: CronogramaProps) {
  return (
    <ul aria-label={etiqueta} className="flex flex-col gap-4">
      {etapas.map((e) => {
        const avance = Math.min(100, Math.max(0, e.avance));
        return (
          <li key={e.id} className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-[11rem_1fr_auto] sm:items-center">
            <div className="flex items-baseline justify-between gap-2 sm:block">
              <span className="font-medium">{e.nombre}</span>
              <span className="block text-chico text-texto-suave">{e.detalle}</span>
            </div>
            <div className="relative h-4 rounded-full bg-superficie-2">
              <div
                role="progressbar"
                aria-label={`Avance de ${e.nombre}`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={avance}
                className={cn("absolute inset-y-0 overflow-hidden rounded-full", FONDO[e.tono])}
                style={{ left: `${e.izquierda}%`, width: `${e.ancho}%` }}
              >
                <div className={cn("h-full rounded-full", RELLENO[e.tono])} style={{ width: `${avance}%` }} />
              </div>
              {hoy !== null && <div data-testid="hoy" aria-hidden className="absolute -inset-y-1 w-px bg-texto" style={{ left: `${hoy}%` }} />}
            </div>
            <div>
              <Badge tono={e.tono}>{e.estado}</Badge>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
